import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../api/axios';

const roleIdMap = {
  1: 'super_admin',
  2: 'system_admin',
  3: 'tca',
  4: 'invigilator'
};

const roleNameToKey = {
  'Super Admin':  'super_admin',
  'System Admin': 'system_admin',
  'TCA':          'tca',
  'Invigilator':  'invigilator'
};

const roleLabel = {
  super_admin:  'Super Admin',
  system_admin: 'System Admin',
  tca:          'TCA',
  invigilator:  'Invigilator'
};

const roleColor = {
  super_admin:  { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  system_admin: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  tca:          { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  invigilator:  { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' }
};

const roleIcon = {
  super_admin:  '👑',
  system_admin: '🛡️',
  tca:          '🎓',
  invigilator:  '👤'
};

const labelStyle = {
  display: 'flex', alignItems: 'center', gap: '5px',
  fontSize: '11px', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
  marginBottom: '7px'
};

const readBoxStyle = {
  padding: '10px 14px',
  backgroundColor: '#f8fafc',
  border: '1px solid #f1f5f9',
  borderRadius: '9px',
  fontSize: '14px', color: '#374151', fontWeight: 500
};

const getInputStyle = () => ({
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #e2e8f0',
  borderRadius: '9px', fontSize: '14px',
  backgroundColor: 'white',
  color: '#0f172a', boxSizing: 'border-box',
  outline: 'none',
  fontFamily: "'Segoe UI', sans-serif"
});

const normalizeUser = (raw) => {
  if (!raw) return null;
  const roleKey =
    roleNameToKey[raw.role_name] ||
    roleIdMap[Number(raw.role_id)] ||
    raw.role ||
    null;
  return {
    ...raw,
    role:       roleKey,
    phone:      raw.phone      || '',
    department: raw.department || '',
    email:      raw.email      || '',
    name:       raw.name       || '',
    created_at: raw.created_at || raw.createdAt || null,
  };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const ReadBlock = ({ label, icon, value }) => (
  <div>
    <div style={labelStyle}><span>{icon}</span>{label}</div>
    <div style={readBoxStyle}>{value || '—'}</div>
  </div>
);

const Profile = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile]   = useState(null);
  const [editing, setEditing]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [loading, setLoading]   = useState(true);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', department: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        // ✅ id is stored as string in localStorage — use it directly
        const userId = user?.id;
        if (!userId) {
          throw new Error('No user ID in session');
        }

        const res = await axios.get(`/users/${userId}`);
        const normalized = normalizeUser(res.data);
        setProfile(normalized);
        setForm({
          name:       normalized.name       || '',
          email:      normalized.email      || '',
          phone:      normalized.phone      || '',
          department: normalized.department || '',
        });
      } catch (err) {
        console.error('Profile fetch failed:', err?.response?.status, err?.response?.data);
        toast.error(`Could not load profile from server: ${err?.response?.data?.message || err.message}`);
        // ✅ Fallback: use whatever is in auth context
        const normalized = normalizeUser(user);
        setProfile(normalized);
        setForm({
          name:       normalized?.name       || '',
          email:      normalized?.email      || '',
          phone:      normalized?.phone      || '',
          department: normalized?.department || '',
        });
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) fetchProfile();
    else setLoading(false);
  }, [user?.id]);

  const data = profile || normalizeUser(user);

  const initials = data?.name
    ? data.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const rc = roleColor[data?.role] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  const ri = roleIcon[data?.role]  || '👤';
  const rl = roleLabel[data?.role] || data?.role_name || data?.role || '—';

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.name.trim())  { toast.error('Name is required');  return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`/users/${data.id}`, {
        name:       form.name.trim(),
        email:      form.email.trim(),
        phone:      form.phone.trim(),
        department: form.department.trim(),
        is_active:  data.is_active ?? 1,
        role_id:    data.role_id,
      });
      const updated = normalizeUser(res.data);
      setProfile(updated);
      // ✅ Also update name in auth context so navbar reflects change
      if (setUser) setUser(prev => ({ ...prev, name: updated.name }));
      setForm({
        name:       updated.name       || '',
        email:      updated.email      || '',
        phone:      updated.phone      || '',
        department: updated.department || '',
      });
      toast.success('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name:       data?.name       || '',
      email:      data?.email      || '',
      phone:      data?.phone      || '',
      department: data?.department || '',
    });
    setEditing(false);
  };

  const EditableField = ({ label, icon, name, type, placeholder }) => (
    <div>
      <div style={labelStyle}><span>{icon}</span>{label}</div>
      {editing ? (
        <input
          name={name}
          type={type || 'text'}
          value={form[name]}
          onChange={handleChange}
          placeholder={placeholder}
          style={getInputStyle()}
        />
      ) : (
        <div style={readBoxStyle}>{data?.[name] || '—'}</div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Segoe UI', sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
          <div style={{ color: '#64748b', fontSize: '15px', fontWeight: 600 }}>Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: '#f1f5f9',
      padding: '32px 20px', fontFamily: "'Segoe UI', sans-serif"
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Page Header */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{ fontSize: '22px' }}>👤</span>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#0f172a' }}>
              My Profile
            </h1>
          </div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px', paddingLeft: '32px' }}>
            Manage your personal information and account settings
          </p>
        </div>

        {/* Hero Banner */}
        <div style={{
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #f1870e 0%, #fb923c 60%, #fbbf24 100%)',
          padding: '36px 40px', marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(241,135,14,0.3)',
          position: 'relative', overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: '-50px', right: '-50px',
            width: '200px', height: '200px', borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.07)'
          }} />
          <div style={{
            position: 'absolute', bottom: '-60px', right: '100px',
            width: '150px', height: '150px', borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.05)'
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap', position: 'relative' }}>
            <div style={{
              width: '90px', height: '90px', borderRadius: '22px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: '3px solid rgba(255,255,255,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '32px', fontWeight: 900, color: 'white',
              flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }}>
              {initials}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '26px', fontWeight: 800, color: 'white', marginBottom: '8px' }}>
                {data?.name || '—'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>✉️ Email</span>
                  <span style={{ fontWeight: 600 }}>{data?.email || '—'}</span>
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>📞 Phone</span>
                  <span style={{ fontWeight: 600 }}>{data?.phone || '—'}</span>
                </div>
                {data?.department && (
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>🏢 Dept</span>
                    <span style={{ fontWeight: 600 }}>{data.department}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  backgroundColor: 'rgba(255,255,255,0.22)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '20px', padding: '4px 14px',
                  fontSize: '13px', fontWeight: 700, color: 'white'
                }}>
                  {ri} {rl}
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  backgroundColor: 'rgba(255,255,255,0.22)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '20px', padding: '4px 14px',
                  fontSize: '13px', fontWeight: 600, color: 'white'
                }}>
                  🆔 ID #{data?.id || '—'}
                </span>
              </div>
            </div>

            {!editing && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: 'white', color: '#f1870e',
                  border: 'none', padding: '11px 22px',
                  borderRadius: '12px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '14px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
                }}
              >
                ✏️ Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 300px',
          gap: '20px', alignItems: 'start'
        }}>

          {/* LEFT — Account Information */}
          <div style={{
            backgroundColor: 'white', borderRadius: '16px',
            boxShadow: '0 1px 8px rgba(0,0,0,0.08)', overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px 28px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  backgroundColor: '#fff7ed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px'
                }}>📋</div>
                <span style={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>Account Information</span>
              </div>
              <span style={{
                backgroundColor: rc.bg, color: rc.color,
                border: `1px solid ${rc.border}`,
                padding: '4px 14px', borderRadius: '20px',
                fontSize: '12px', fontWeight: 700
              }}>
                {ri} {rl}
              </span>
            </div>

            <div style={{ padding: '28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <EditableField label="Full Name"     icon="👤" name="name"       placeholder="Enter full name" />
                <EditableField label="Email Address" icon="✉️" name="email"      type="email" placeholder="Enter email address" />
                <EditableField label="Phone Number"  icon="📞" name="phone"      placeholder="Enter phone number" />
                <EditableField label="Department"    icon="🏢" name="department" placeholder="Enter department" />
                <ReadBlock label="Role"         icon="🎭" value={rl} />
                <ReadBlock label="User ID"      icon="🆔" value={data?.id ? `#${data.id}` : null} />
                <ReadBlock label="Member Since" icon="📅" value={formatDate(data?.created_at)} />
                <div>
                  <div style={labelStyle}><span>🟢</span> Status</div>
                  <div style={readBoxStyle}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      backgroundColor: data?.is_active === 0 ? '#fee2e2' : '#d1fae5',
                      color: data?.is_active === 0 ? '#991b1b' : '#065f46',
                      padding: '3px 12px', borderRadius: '20px',
                      fontSize: '12px', fontWeight: 700
                    }}>
                      ● {data?.is_active === 0 ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>

              {editing && (
                <div style={{
                  display: 'flex', gap: '12px', marginTop: '28px',
                  paddingTop: '24px', borderTop: '1px solid #f1f5f9'
                }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '13px',
                      background: 'linear-gradient(135deg, #f1870e, #fb923c)',
                      color: 'white', border: 'none', borderRadius: '10px',
                      fontSize: '15px', fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.7 : 1,
                      boxShadow: '0 4px 12px rgba(241,135,14,0.35)'
                    }}
                  >
                    {saving ? '⏳ Saving...' : '💾 Save Changes'}
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: '13px 28px',
                      backgroundColor: '#f8fafc', color: '#64748b',
                      border: '1px solid #e2e8f0', borderRadius: '10px',
                      fontSize: '15px', fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Quick Actions */}
            <div style={{
              backgroundColor: 'white', borderRadius: '16px',
              boxShadow: '0 1px 8px rgba(0,0,0,0.08)', overflow: 'hidden'
            }}>
              <div style={{
                padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '9px',
                  backgroundColor: '#fff7ed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px'
                }}>⚡</div>
                <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>Quick Actions</span>
              </div>
              <div style={{ padding: '16px' }}>
                <button
                  onClick={() => navigate('/change-password')}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 16px', borderRadius: '12px',
                    backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    backgroundColor: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '18px', flexShrink: 0,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                  }}>🔒</div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Change Password</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '1px' }}>Update your password</div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: '#cbd5e1', fontSize: '18px' }}>›</span>
                </button>
              </div>
            </div>

            {/* Account Summary */}
            <div style={{
              backgroundColor: 'white', borderRadius: '16px',
              boxShadow: '0 1px 8px rgba(0,0,0,0.08)', overflow: 'hidden'
            }}>
              <div style={{
                padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '9px',
                  backgroundColor: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px'
                }}>📊</div>
                <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>Account Summary</span>
              </div>
              <div style={{ padding: '8px 20px 16px' }}>
                {[
                  { label: 'Account ID',  value: `#${data?.id || '—'}`,       icon: '🆔' },
                  { label: 'Role',        value: rl,                           icon: '🎭' },
                  { label: 'Department',  value: data?.department || '—',      icon: '🏢' },
                  { label: 'Email',       value: data?.email      || '—',      icon: '✉️' },
                  { label: 'Phone',       value: data?.phone      || '—',      icon: '📞' },
                  { label: 'Joined',      value: formatDate(data?.created_at), icon: '📅' },
                ].map(({ label, value, icon }) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '11px 0', borderBottom: '1px solid #f8fafc'
                  }}>
                    <span style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {icon} {label}
                    </span>
                    <span style={{
                      fontSize: '13px', fontWeight: 600, color: '#0f172a',
                      maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;